import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Users, Dumbbell } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface ClassOccupancy {
  classId: string;
  title: string;
  instructor: string;
  capacity: number;
  subscriptions: number;
  occupancyRate: number;
}

interface ClassOccupancyCardProps {
  gymId: string;
}

export const ClassOccupancyCard = ({ gymId }: ClassOccupancyCardProps) => {
  const { data: classes, isLoading } = useQuery({
    queryKey: ["gym-class-occupancy", gymId],
    queryFn: async (): Promise<ClassOccupancy[]> => {
      const { data } = await supabase
        .from("classes")
        .select(`
          id,
          title,
          instructor,
          capacity,
          class_subscriptions(count)
        `)
        .eq("gym_id", gymId)
        .order("title");

      return (data || []).map(cls => ({
        classId: cls.id,
        title: cls.title,
        instructor: cls.instructor,
        capacity: cls.capacity,
        subscriptions: cls.class_subscriptions?.[0]?.count || 0,
        occupancyRate: cls.capacity > 0 
          ? ((cls.class_subscriptions?.[0]?.count || 0) / cls.capacity) * 100 
          : 0,
      }));
    },
    enabled: !!gymId,
  });

  const getOccupancyColor = (rate: number) => {
    if (rate >= 80) return "bg-red-500";
    if (rate >= 50) return "bg-yellow-500";
    return "bg-green-500";
  };

  if (isLoading) {
    return (
      <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Dumbbell className="h-5 w-5 text-orange-500" />
            Ocupación de Clases
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-muted/30 rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Dumbbell className="h-5 w-5 text-orange-500" />
          Ocupación de Clases
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!classes || classes.length === 0 ? (
          <p className="text-muted-foreground text-sm py-4 text-center">
            No hay clases registradas
          </p>
        ) : (
          <div className="space-y-4">
            {classes.slice(0, 5).map(cls => (
              <div key={cls.classId} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-foreground text-sm">{cls.title}</p>
                    <p className="text-xs text-muted-foreground">{cls.instructor}</p>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-3 w-3 text-muted-foreground" />
                    <span className="text-foreground">
                      {cls.subscriptions}/{cls.capacity}
                    </span>
                    <span className="text-muted-foreground">
                      ({cls.occupancyRate.toFixed(0)}%)
                    </span>
                  </div>
                </div>
                <Progress 
                  value={cls.occupancyRate} 
                  className={`h-2 ${getOccupancyColor(cls.occupancyRate)}`}
                />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
